<script lang="ts">
  import { onMount } from "svelte";
  import { Button } from "$lib/components/ui/button/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import { Label } from "$lib/components/ui/label/index.js";
  import * as Sidebar from "$lib/components/ui/sidebar/index.js";
  import {
    getSystemSettings,
    saveSystemSettings
  } from "$lib/services/system-settings-service";
  import type { SystemSetting } from "$lib/types/system-setting";

  let settings: SystemSetting[] = [];
  let isLoading = true;
  let isSaving = false;
  let errorMessage = "";
  let successMessage = "";

  const boolToString = (value: string | null) => {
    if (value === null) return "";
    const normalized = value.toLowerCase();
    if (normalized === "true") return "true";
    if (normalized === "false") return "false";
    return "";
  };

  const loadSettings = async () => {
    isLoading = true;
    errorMessage = "";
    successMessage = "";
    try {
      settings = await getSystemSettings();
    } catch {
      errorMessage = "Failed to load settings.";
    } finally {
      isLoading = false;
    }
  };

  const saveAll = async () => {
    isSaving = true;
    errorMessage = "";
    successMessage = "";
    try {
      await saveSystemSettings(settings);
      await loadSettings();
      successMessage = "Settings saved.";
    } catch {
      errorMessage = "Failed to save settings.";
    } finally {
      isSaving = false;
    }
  };

  onMount(() => {
    void loadSettings();
  });

  const handleBoolSettingChange = (setting: SystemSetting, event: Event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLSelectElement)) {
      return;
    }

    setting.value = target.value === "" ? null : target.value;
  };

  const handleIntSettingInput = (setting: SystemSetting, event: Event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    setting.value = target.value === "" ? null : target.value;
  };
</script>

<Sidebar.Inset>
  <header class="flex h-16 items-center gap-2 border-b border-border px-4">
    <Sidebar.Trigger class="md:hidden" />
    <h2 class="text-lg font-semibold">Settings</h2>
  </header>
  <section class="p-6">
    {#if isLoading}
      <p class="text-sm text-muted-foreground">Loading settings...</p>
    {:else}
      <div class="space-y-5">
        {#each settings as setting, i (setting.identifier)}
          <div class="space-y-2">
            <Label for={`setting-${i}`}>{setting.identifier}</Label>
            {#if setting.valueType === "bool"}
              <select
                id={`setting-${i}`}
                class="h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={boolToString(setting.value)}
                onchange={(event) => handleBoolSettingChange(setting, event)}
              >
                <option value="">Select...</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            {:else if setting.valueType === "int"}
              <Input
                id={`setting-${i}`}
                type="number"
                step="1"
                value={setting.value ?? ""}
                oninput={(event) => handleIntSettingInput(setting, event)}
              />
            {:else}
              <Input id={`setting-${i}`} bind:value={setting.value} />
            {/if}
          </div>
        {/each}

        <div class="flex items-center gap-3 pt-2">
          <Button type="button" onclick={saveAll} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save all"}
          </Button>
          {#if successMessage}
            <span class="text-sm text-green-600">{successMessage}</span>
          {/if}
          {#if errorMessage}
            <span class="text-sm text-destructive">{errorMessage}</span>
          {/if}
        </div>
      </div>
    {/if}
  </section>
</Sidebar.Inset>
